import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DateAdapter, MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { format, isValid, parseISO } from "date-fns";
import dayjs from "dayjs/esm";
import { NgxMaskDirective } from "ngx-mask";
import { filter } from "rxjs";
import { DateFormat, DateSeparator, ISODateFormat } from "src/app/common/constants/core-constants";
import { AppConstants } from "src/app/common/constants/app-constants";
import { PATTERN } from "src/app/common/constants/patterns";

@UntilDestroy()
@Component({
  selector: "app-date-picker",
  templateUrl: "./date-picker.component.html",
  styleUrls: ["./date-picker.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMaskDirective,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class DatePickerComponent implements OnInit, OnChanges {
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() placeholder: string = DateFormat.toUpperCase();
  @Input() disabled: boolean = false;
  @Input() width: "full" | "none" = "none";
  @Input() required: boolean = false;
  @Input() showError: boolean = true;

  @Output() dateChange = new EventEmitter<Date>();
  @Input() label: string = '';
  @Input() minDateReadable: string | null = '';
  @Input() maxDateReadable: string | null = '';

  
  dateVal: any;
  dateValFormatted: any;
  currentDate!: Date;

  ngOnInit() {
  this.currentDate = new Date();
   if(this.minDate) this.minDate = this.getDateObject(this.minDate);
   if(this.maxDate) this.maxDate = this.getDateObject(this.maxDate);

   const controlValue = this.formGroup.get(this.controlName)?.value;
   if (controlValue) {
     this.dateVal = this.getDateObject(controlValue);
     this.dateValFormatted = this.getFormattedDate(this.dateVal);
     this.formGroup.get(this.controlName)?.setValue(this.dateVal);
   }

    // listen for value changes outside of the component and update the dateVal and dateValFormatted
    this.formGroup
      .get(this.controlName)
      ?.valueChanges.pipe(
        filter((newValue) => newValue !== this.dateVal),
        untilDestroyed(this)
      )
      .subscribe((val) => {
        if (val) {
          this.dateVal =val;
          this.dateValFormatted = this.getFormattedDate(this.dateVal);
        }
        else {
          this.dateVal = null;
          this.dateValFormatted = '';
        }
      });
  }

  onDatePickerToggle() {
    const controlValue = this.formGroup.get(this.controlName)?.value;
    if (controlValue) {
      this.dateVal = this.getDateObject(controlValue);
      this.dateValFormatted = this.getFormattedDate(this.dateVal);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-validate current date when min/max dates change
    if ((changes['minDate'] || changes['maxDate']) && this.dateVal) {
      const dateRangeError = this.validateDateRange(this.dateVal);
      const control = this.formGroup.get(this.controlName);
      
      if (dateRangeError) {
        control?.markAsTouched();
        control?.setErrors(dateRangeError);
      } else {
        // Clear date range errors if they were previously set
        const currentErrors = control?.errors;
        if (currentErrors && (currentErrors['mindate'] || currentErrors['maxdate'])) {
          const newErrors = { ...currentErrors };
          delete newErrors['mindate'];
          delete newErrors['maxdate'];
          control?.setErrors(Object.keys(newErrors).length > 0 ? newErrors : null);
        }
      }
    }

    // Update displayed values when control value changes externally
    const controlValue = this.formGroup.get(this.controlName)?.value;
    if (controlValue) {
      this.dateVal = this.getDateObject(controlValue);
      this.dateValFormatted = this.getFormattedDate(this.dateVal);
    } else {
      this.dateVal = null;
      this.dateValFormatted = '';
    }
  }

  maskChange() {
    const date = this.dateValFormatted;

    const alreadyFormatted = date.includes("/");

    const formatted = alreadyFormatted
      ? date
      : `${date.substring(0, 2)}/${date.substring(2, 4)}/${date.substring(4,9)}`;

    const control = this.formGroup.get(this.controlName);
    const formattedDateObject = this.getDateObject(formatted);
    if(control && ((control.value && formattedDateObject) && isValid(formattedDateObject) && isValid(this.getDateObject(control.value)) && format(this.getDateObject(control.value), ISODateFormat) !== format(formattedDateObject, ISODateFormat)) || (control?.value && formatted === DateSeparator) || (!formatted && formatted !== DateSeparator && !control?.value))
      {
      control?.markAsDirty();
      control?.markAsTouched();
    }

    if (isValid(formattedDateObject)) {
      const dateRangeError = this.validateDateRange(formattedDateObject);
      control?.setValue(formattedDateObject);
      this.dateVal = formattedDateObject;
      this.dateChange.emit(this.dateVal);
      if (dateRangeError) {
        control?.setErrors(dateRangeError);
      }
    } 
    else {
      control?.setValue(null);
      if (this.required) {
        control?.setErrors({ required: true });
      }
      this.dateVal = "";
      this.dateValFormatted = "";
      this.dateChange.emit(undefined);
    }
  }

  dateSelected() {
    if (this.dateVal) {
      this.dateValFormatted = this.getFormattedDate(this.dateVal);
    }

    const control = this.formGroup.get(this.controlName);
    control?.markAsDirty();
    control?.markAsTouched();

    control?.setValue(this.dateVal);
    this.dateChange.emit(this.dateVal);

    if (this.dateVal) {
      const dateRangeError = this.validateDateRange(this.dateVal);
      if (dateRangeError) {
        control?.setErrors(dateRangeError);
      }
    }
  }

  private getDateObject(value : Date | string) : Date {
    if (typeof value === "string") {
      return this.getDateFromISOString(value);
    }
    const date = new Date(value);
    return new Date(date?.getFullYear(), date?.getMonth(), date?.getDate());
  }

  private getFormattedDate(date : Date) : string | Date{
    return format(this.getDateObject(date),DateFormat);
  }

  highlightCustomToday = (date: Date): string => {
    const result = this.currentDate && date?.toDateString() === this.currentDate?.toDateString() ? AppConstants.customToday : AppConstants.emptyString;
    return result
  };

  getDateFromISOString(isoDateString: string): Date {
    if (PATTERN.ISO_DATE_ONLY_REGEX.test(isoDateString)) {
      const [year, month, day] = isoDateString.split("-").map(Number);
      return new Date(year, month - 1, day); // local date, no timezone shift
    }
    const date = new Date(isoDateString);
    return new Date(date?.getFullYear(), date?.getMonth(), date?.getDate());
  }

  private validateDateRange(dateValue: Date | null): { [key: string]: boolean } | null {
    if(!dateValue) {
      return null;
    }
    
    if (this.minDate && dateValue < this.minDate) {
      return { mindate: true };
    }
    
    if (this.maxDate && dateValue > this.maxDate) {
      return { maxdate: true };
    }
    
    return null;
  }

  get minDateReadableDisplay(): string {
    if (this.minDateReadable) {
      return this.minDateReadable;
    }
    if (this.minDate) {
      return format(this.minDate, DateFormat);
    }
    return '';
  }

  get maxDateReadableDisplay(): string {
    if (this.maxDateReadable) {
      return this.maxDateReadable;
    }
    if (this.maxDate) {
      return format(this.maxDate, DateFormat);
    }
    return '';
  }

}